<template>
  <div class="connections-table">
    <div v-if="allConnections.length === 0" class="no-connections-text">
      <BaseIcon name="noConfigurations" />
      <h4 class="text-white">
        {{ this.$t('aws.noAwsConfigurations') }}
      </h4>
      <div>{{ this.$t('aws.noAwsConfigurationsDescription') }}</div>
      <div>
        <RexButton
          id="add-new-configuration-btn"
          variant="secondary"
          @onClick="handleAddConfigurationClick"
        >
          +
          {{ $t('aws.addConfiguration') }}
        </RexButton>
      </div>
    </div>
    <RexTableCompV2
      v-if="allConnections.length > 0"
      :headers="tableHeader"
      :items="allConnections"
      variant="primary"
      :loading="loading"
      height="auto"
      :enableDetails="true"
      :striped="false"
      :enableInfiniteScroll="false"
    >
      <template
        v-for="header in tableHeader"
        #[`header-${header.key}`]
        :key="header.key"
      >
        <div>
          {{ header.name }}
        </div>
      </template>
      <template
        v-for="(header, index) in tableHeader"
        :key="index"
        #[`body-${header.key}`]="{ item }"
      >
        <div
          v-if="header.key !== 'services'"
          :key="header.key"
          class="ellipsis-content"
        >
          {{ item[header.key] }}
        </div>
        <div
          v-if="header.key === 'services'"
          :key="header.key"
          class="table-services"
        >
          <div v-for="service in item.services" :key="service.id">
            <RexTooltipV3 :title="getServiceNameMapper(service.serviceName)">
              <img
                style="height: 20px; width: 20px"
                :src="
                  require(`@/assets/img/aws-${service.serviceNameSlug}.png`)
                "
              />
            </RexTooltipV3>
          </div>
        </div>
        <div
          v-if="header.key === 'actionItems'"
          :key="header.key"
          class="table-actions"
        >
          <TableActions
            :item="item"
            @edit="handleEdit"
            @delete="handleDelete"
            @view="handleView"
            @forceUpdate="handleForceUpdate"
          />
        </div>
      </template>
    </RexTableCompV2>
    <DeleteModal
      v-if="showDeleteModal"
      :connectionName="connectionName"
      :subscriptionId="subscriptionId"
      :showModal="showDeleteModal"
      @deleteConfirmed="handleDeleteItem"
      @close="handleCloseDeleteModal"
    />
  </div>
</template>

<script>
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import RexButton from '@/rex-components/rex-button.vue';
import {
  serviceNames,
  TableHeaders,
} from '@/components/Cloud/services/aws/aws.services.types';
import TableActions from '@/components/Cloud/services/aws/aws-table-actions.vue';
import DeleteModal from '@/components/Cloud/services/aws/aws-delete-connnection-table.vue';
import { mapActions, mapState } from 'vuex';
import { GET_ALL_CONNECTIONS } from '@/store/modules/cloud/connections/services.constants';
import RexTooltipV3 from '@/rex-components/RexTooltipV3.vue';
import { CloudService } from '@/services/api/integration.service';
import { notificationError, notificationSucess } from '@/utils/notification';

export default {
  components: {
    RexTableCompV2,
    TableActions,
    DeleteModal,
    RexButton,
    RexTooltipV3,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    stopInfiniteScroll: {
      type: Boolean,
      default: false,
    },
    isError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['addConfigurationClick', 'handleEdit'],
  data() {
    return {
      tableHeader: TableHeaders,
      isAsc: false,
      sortingField: 'createdAt',
      showDeleteModal: false,
      connectionName: '',
      subscriptionId: '',
    };
  },
  computed: {
    ...mapState('cloudState/connections', ['allConnections']),
  },
  methods: {
    ...mapActions('cloudState/connections', [GET_ALL_CONNECTIONS]),
    handleEdit(payload) {
      this.$emit('handleEdit', payload);
    },
    handleDelete(payload) {
      this.connectionName = payload.name;
      this.subscriptionId = payload.subscriptionId;
      this.showDeleteModal = true;
    },
    getServiceNameMapper(name) {
      return serviceNames[name];
    },
    handleView(payload) {
      this.$router.push(`/integrations/aws/${payload.subscriptionId}`);
    },
    async handleForceUpdate(payload) {
      try {
        const subscriptionId = payload.subscriptionId;
        const data = await CloudService.getForceUpdate({ subscriptionId });
        if (data.success) {
          notificationSucess(this.$t(`aws.forceUpdateSuccess`));
        } else {
          notificationError(this.$t(`aws.forceUpdateFailure`));
        }
      } catch (e) {
        console.log(e);
        notificationError(this.$t(`aws.forceUpdateFailure`));
      }
    },
    handleDeleteItem() {
      this.showDeleteModal = false;
    },
    handleCloseDeleteModal() {
      this.showDeleteModal = false;
    },
    handleAddConfigurationClick() {
      this.$emit('addConfigurationClick');
    },
  },
  beforeMount() {
    // Call the action when the component is created or as needed
    this[GET_ALL_CONNECTIONS]();
  },
};
</script>

<style lang="scss" scoped>
.no-connections-text {
  width: 18.75rem;
  gap: 0.75rem;
  @include flex-center-column;
}

#add-new-configuration-btn {
  width: auto !important;
  padding: 0.5rem 1.25rem;
  @include flex-center;
}

.connections-table {
  @include flex-center-justify(center);
}

.table-services {
  display: flex;
  gap: 0.3125rem;
}
</style>
